import { COORDINATE_STOCKHOLM } from '../../constants';
import { Coordinate } from '@shared/models/geography.model';
import { getCoordinateDistance } from '../geometry/coordinates.helpers';
import { cityClimateDataList } from './data/city-climate.data';
import { ICityClimateData } from './energy.models';

export type ICoordinateOrValue = Coordinate | number;
/**
 * Get an "average" form values in cityClimateList based on coordinate distance.
 * @param target - The coordinate to get the average for or an already calculated value
 * @param valueFn - The function to get the value from the cityClimateList
 * @returns The average value
 */
const getDistanceWeightedAverage = (
  target: ICoordinateOrValue,
  valueFn: (item: ICityClimateData) => number | undefined,
): number => {
  if (typeof target === 'number') {
    return target;
  }
  let tempValue = 0;
  let weights = 0;
  for (const city of cityClimateDataList) {
    const value = valueFn(city);
    if (value === undefined) {
      continue;
    }
    const distance = getCoordinateDistance(target, city) / 1000; // distance in km
    const weight = 1 / Math.pow(distance, 2); // viktar med 1/avstånd^2, dvs punkter som ligger nära får mkt större betydelse
    tempValue += value * weight;
    weights += weight;
  }
  return tempValue / weights;
};

/**
 * Get the normal year temperature (median temperature) for a given coordinate.
 * Is based on the cities in the table (only works decently for Sweden).
 * @param lat
 * @param lng
 * @returns
 */
export const getNormalYearTemp = (
  target: ICoordinateOrValue = COORDINATE_STOCKHOLM,
): number => getDistanceWeightedAverage(target, (city) => city.temp);

/**
 * Get the design outdoor temperature (DUT). In swedish "Dimensionerande utetemperatur (DUT)" or "Dimensionerande vinterutetemperatur (DVUT)".
 * Basically the lowest temperature that the heating system needs to be able to handle (currently for 3 days in a row).
 * Is based on the cities in the table (only works decently for Sweden).
 * @param target - The coordinate to get the DUT for
 * @returns The DUT in °C
 */
export const getDesignOutdoorTemp = (target: ICoordinateOrValue): number =>
  getDistanceWeightedAverage(target, (city) => city.dot);

/**
 * Get the geographical adjustment factor (Fgeo).
 * City values at Page 143: https://www.boverket.se/globalassets/publikationer/dokument/2020/konsoliderad-bbr-2011-6-tom-2020-4.pdf
 * @param target - The coordinate to get the Fgeo for
 * @returns The Fgeo
 */
export const getGeographicalAdjustmentFactor = (
  target: ICoordinateOrValue,
): number => getDistanceWeightedAverage(target, (city) => city.fGeo);

/**
 * Get the average cooling hours per year for a given coordinate.
 * Is based on the cities in the table (only works decently for Sweden).
 * @param target - The coordinate to get the average cooling hours for
 * @returns The average cooling hours
 */
export const getCoolingHours = (target: ICoordinateOrValue): number =>
  getDistanceWeightedAverage(target, (city) => city.coolingHours);
